import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getIncidenceReviews } from '../../services/incidenceService';

const STATUS_LABELS = {
    pending: 'Pendiente',
    reviewed: 'Revisada',
    closed: 'Cerrada',
};

const STATUS_BADGES = {
    pending: 'bg-warning text-dark',
    reviewed: 'bg-info text-white',
    closed: 'bg-success text-white',
};

const ReviewHistory = ({ incidenceId }) => {
    const { t } = useTranslation();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!incidenceId) return;

        const ac = new AbortController();
        setLoading(true);
        setError(null);

        getIncidenceReviews(incidenceId, { signal: ac.signal })
            .then((data) => {
                setReviews(data);
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    setError(err.message);
                }
            })
            .finally(() => setLoading(false));

        return () => ac.abort();
    }, [incidenceId]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) {
        return (
            <div className="text-center p-3">
                <div
                    style={{
                        width: '1.5rem',
                        height: '1.5rem',
                        border: '3px solid #e9ecef',
                        borderTop: '3px solid #6c757d',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                        margin: '0 auto',
                    }}
                    role="status"
                />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger p-2 m-2" role="alert">
                {t('Error loading reviews')}
            </div>
        );
    }

    if (reviews.length === 0) {
        return (
            <div className="text-center text-muted p-3">
                <i className="bi bi-journal-text me-1"></i>
                {t('No reviews yet')}
            </div>
        );
    }

    return (
        <div className="review-history p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {reviews.map((review) => (
                <div
                    key={review.id}
                    className="card mb-2"
                    style={{ fontSize: '0.85rem' }}
                >
                    <div className="card-body p-2">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                            <strong>
                                <i className="bi bi-person-fill me-1"></i>
                                {review.changed_by_user?.name || review.changed_by?.name || t('Unknown user')}
                            </strong>
                            <small className="text-muted">
                                {formatDate(review.created_at)}
                            </small>
                        </div>
                        <div className="mb-1">
                            <span className={`badge ${STATUS_BADGES[review.old_status] || 'bg-secondary'} me-1`}
                                style={{ fontSize: '0.7rem' }}>
                                {t(STATUS_LABELS[review.old_status] || review.old_status)}
                            </span>
                            <i className="bi bi-arrow-right mx-1" style={{ fontSize: '0.7rem' }}></i>
                            <span className={`badge ${STATUS_BADGES[review.new_status] || 'bg-secondary'}`}
                                style={{ fontSize: '0.7rem' }}>
                                {t(STATUS_LABELS[review.new_status] || review.new_status)}
                            </span>
                        </div>
                        {review.comment && (
                            <p className="mb-0 mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                                {review.comment}
                            </p>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ReviewHistory;
